import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { FormikHelpers } from "formik";
import { useRouteHelpers } from "routes/helpers";
import routes from "routes/routes";

import { logout } from "services/auth";
import { EMagicLinkFlow } from "types/AuthFlowTypes";
import { ErrorConst, MFAFactorType } from "types/BETypes";
import { COMPANIES_LIST_FETCH_ALL_LIMIT } from "constants/company/companiesList";
import { HELP_EMAIL } from "constants/shared";
import { showErrorModal } from "helpers";
import { hasUserMultipleCompanies } from "helpers/auth/company";
import useAuth from "hooks/useAuth";
import { useCompaniesList } from "hooks/useCompaniesList";
import useMFASMS from "components/MultiFactorAuthorization/components/sms/useMFASMS";
import { MFAWidgetType } from "components/MultiFactorAuthorization/types";

import { mutationAuthControllerMfaLogin } from "utils/swagger_react_query";

import { MfaSignInFormType } from "./components";
import { ISignInMfaPageNavigationParams } from "./types";

export const useSignInMfaPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const locationState = useLocation().state as unknown as ISignInMfaPageNavigationParams;

  const [isPageDataLoading, setPageDataLoading] = useState<boolean>(true);
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [svoc, setSvoc] = useState<boolean>(false);

  const {
    currentFactor,
    currentFactorChallenge,
    createFactorChallenge,
    resendVerificationCode,
    fetchData,
    factorsData,
    selectFactor,
    errorsPrefix: mfaErrorsPrefix,
  } = useMFASMS({
    widgetType: MFAWidgetType.MISC,
    fetchDataOnMount: false,
  });
  const { selectActiveCompany } = useAuth();
  const {
    actions: { refetchCompanies },
  } = useCompaniesList({ perPage: COMPANIES_LIST_FETCH_ALL_LIMIT });

  const { getDefaultRoute } = useRouteHelpers();

  const initialValues: MfaSignInFormType = {
    code: "",
  };

  useEffect(() => {
    if (!locationState?.code && !locationState?.invitationId) {
      logout();
    }
    initFetch();
  }, []);

  const initFetch = async () => {
    try {
      setPageDataLoading(true);
      const factorsRes = await fetchData();
      const smsFactor = factorsRes?.find((factor) => factor.type === MFAFactorType.SMS);
      if (!smsFactor) throw new Error(ErrorConst.MFA_FACTOR_NOT_FOUND);
      selectFactor(smsFactor);
      await createFactorChallenge(smsFactor?.factorId || "");
    } catch (error) {
      showErrorModal(error, logout);
    } finally {
      setPageDataLoading(false);
    }
  };

  const handleSubmit = async (
    values: MfaSignInFormType,
    formikHelpers: FormikHelpers<MfaSignInFormType>,
  ) => {
    const { setFieldError } = formikHelpers;
    try {
      setSubmitting(true);
      await mutationAuthControllerMfaLogin()({
        challengeId: currentFactorChallenge?.challengeId || "",
        code: values.code,
      });

      const companiesListRes = await refetchCompanies(undefined, true);
      const companyIds = companiesListRes?.companies?.map((it) => it.companyId) || [];
      const userHasMultipleCompanies = hasUserMultipleCompanies(companyIds);

      const isOwnerSignUpFlow =
        locationState?.mode === EMagicLinkFlow.SIGN_UP && !locationState?.invitationId;

      if (isOwnerSignUpFlow && !!companyIds.length) {
        return navigate(routes.SELECT_COMPANY, {
          replace: true,
        });
      }
      if (!userHasMultipleCompanies) {
        await selectActiveCompany(companyIds?.[0]);
      }
      return navigate(getDefaultRoute(), {
        replace: true,
      });
    } catch (error) {
      setFieldError(
        "code",
        t(`${mfaErrorsPrefix}.${(error as any).data.error}`, { helpEmail: HELP_EMAIL }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyCodeResend = async (callback: () => void) => {
    try {
      setSubmitting(true);
      await resendVerificationCode(callback);
    } catch (error) {
      showErrorModal(error);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    metadata: {
      isLoading: isPageDataLoading,
      isSubmitting,
      svoc,
      setSvoc,
      initialValues,
    },
    pageData: {
      currentFactor,
    },
    actions: {
      handleSubmit,
      handleVerifyCodeResend,
    },
  };
};
